<template>
	<div
		:class="[
			'relative flex flex-col gap-3 bg-won-green-hover px-6 py-4',
			mode === 'summary' ? 'md:py-6' : 'md:pt-10 md:pb-6',
		]"
	>
		<div
			v-if="showToggle"
			:class="[
				collapsible && !detailsOpen ? 'hidden md:flex' : 'flex',
				'justify-center md:absolute md:top-0 md:left-1/2 md:-translate-x-1/2',
			]"
		>
			<BillingCycleToggle
				v-model="cycle"
				:save-percent="savings"
			/>
		</div>

		<div class="flex flex-col gap-3">
			<div :class="['flex flex-col text-center', { 'md:min-h-[68px]': mode !== 'summary' }]">
				<p class="text-xs font-medium text-won-green">
					{{ display.name }}
				</p>
				<p
					v-if="providerName"
					class="py-1 text-xl font-semibold text-gray-900"
				>
					Provided by {{ providerName }}
				</p>
				<Skeleton
					v-else-if="!price"
					class="mx-auto h-9 w-32"
				/>
				<p
					v-else
					class="flex flex-wrap items-end justify-center"
				>
					<span
						:class="[
							'text-3xl leading-9 font-semibold tracking-tight',
							isPriceStruck ? 'text-gray-400 line-through' : 'text-gray-900',
						]"
					>{{ price }}</span>
					<span
						v-if="discountedPrice"
						class="ml-1 text-3xl leading-9 font-semibold tracking-tight text-gray-900"
					>{{ discountedPrice }}</span>
					<span class="text-lg leading-7 text-won-secondary">{{ display.suffix }}</span>
				</p>
				<p
					v-if="showSubLine"
					class="text-xs text-won-secondary opacity-80"
				>
					<span class="line-through">{{ display.strike }}</span> • {{ display.equivalent }}
				</p>
				<p
					v-if="couponDescription"
					class="text-xs text-won-green"
				>
					{{ couponDescription }}
				</p>
			</div>

			<div class="flex flex-col gap-2">
				<div
					v-if="mode !== 'summary' || display.introNote"
					:class="['flex flex-col', { 'md:min-h-[52px]': mode !== 'summary' }]"
				>
					<button
						v-if="mode === 'offer'"
						type="button"
						class="flex h-9 w-full items-center justify-center rounded-md bg-won-green px-3 text-sm font-medium text-white hover:bg-won-green/90"
						@click="emit('select', activeCycle)"
					>
						{{ display.ctaLabel }}
					</button>
					<slot
						v-if="mode === 'current'"
						name="controls"
					/>
					<p
						v-if="display.introNote && mode !== 'current'"
						class="text-center text-[10px] leading-4 text-won-secondary"
					>
						{{ display.introNote }}
					</p>
				</div>
				<PlanDetailsToggle
					v-if="collapsible"
					v-model="detailsOpen"
				/>
			</div>
		</div>

		<div :class="[collapsible && !detailsOpen ? 'hidden md:flex' : 'flex', 'flex-col gap-3']">
			<hr class="border-gray-200" />
			<PlanFeatureGroups
				:groups="PREMIUM_PLAN_GROUPS"
				included
			/>
			<div
				v-if="showPerk"
				class="flex items-start gap-1 border-y border-won-green bg-won-green-light px-3 py-2 text-gray-900"
			>
				<span class="flex h-4 shrink-0 items-center">
					<GiftIcon
						class="size-3 text-won-green"
						aria-hidden="true"
					/>
				</span>
				<div class="flex flex-1 flex-col text-xs">
					<p>First {{ perk.cap }} sign-ups get a free diagnostic call</p>
					<p class="font-medium">
						{{ perk.remaining }} of {{ perk.cap }} left
					</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { GiftIcon } from '@heroicons/vue/24/solid';

import BillingCycleToggle from './billing-cycle-toggle.vue';
import PlanDetailsToggle from './plan-details-toggle.vue';
import PlanFeatureGroups from './plan-feature-groups.vue';
import { Skeleton } from '../../ui/skeleton';
import { setAuthState } from '../../../composables/auth';
import { useSubscriptionPlanStore } from '../../../store/subscription.store';
import { PREMIUM_PLAN_GROUPS } from '../../../constants/pricing-plans';
import {
	BILLING_CYCLE_MONTHLY,
	BILLING_CYCLE_SIX_MONTH,
	formatAmount,
	premiumDisplay,
	savePercent,
} from '../../../utils/premium-pricing';

/**
 * The Premium / God Mode column (EN-1026). One component everywhere the plan is sold, so the
 * Billing page, the checkout summary and the paywalls cannot drift apart.
 *
 * - `offer`: the billing-cycle toggle (when God Mode is on sale) and the upgrade CTA.
 * - `current`: the plan the user already has, with their billing controls in `#controls`.
 * - `summary`: the checkout order summary — the option already chosen, with no toggle.
 */
const props = defineProps({
	mode: {
		type: String,
		default: 'offer',
		validator: (value) => ['offer', 'current', 'summary'].includes(value),
	},
	// On mobile the feature list sits behind "Plan Details" unless this is false.
	collapsible: {
		type: Boolean,
		default: true,
	},
	defaultOpen: {
		type: Boolean,
		default: true,
	},
});

const emit = defineEmits(['select']);

// Read fields directly off the store (it's reactive) instead of storeToRefs(): the store
// exposes a plain `stripeKey` that is undefined when VITE_STRIPE_KEY is unset (e.g. in CI).
const subscriptionStore = useSubscriptionPlanStore();
const { getUser } = setAuthState();
const user = getUser();

const cycle = ref(BILLING_CYCLE_SIX_MONTH);
const detailsOpen = ref(props.defaultOpen);

const options = computed(() => subscriptionStore.premiumOptions);
const perk = computed(() => subscriptionStore.premiumPerk);

const activeCycle = computed(() =>
	cycle.value === BILLING_CYCLE_SIX_MONTH && options.value?.six_month ? BILLING_CYCLE_SIX_MONTH : BILLING_CYCLE_MONTHLY
);

const currentCycle = computed(() =>
	user?.premium_billing_cycle === BILLING_CYCLE_SIX_MONTH ? BILLING_CYCLE_SIX_MONTH : BILLING_CYCLE_MONTHLY
);

const displayedOption = computed(() => {
	if (props.mode === 'summary') {
		return subscriptionStore.selectedOption;
	}

	if (props.mode === 'current') {
		const catalogOption = options.value?.[currentCycle.value];

		return {
			cycle: currentCycle.value,
			amount: user?.original_amount ?? catalogOption?.amount,
			currency: user?.currency ?? catalogOption?.currency,
		};
	}

	return options.value?.[activeCycle.value];
});

const display = computed(() => premiumDisplay(displayedOption.value, options.value?.monthly));
const price = computed(() => formatAmount(displayedOption.value?.amount, displayedOption.value?.currency));
const savings = computed(() => savePercent(options.value?.monthly?.amount, options.value?.six_month?.amount));

const showToggle = computed(() => props.mode === 'offer' && !!options.value?.six_month);
const showSubLine = computed(() => props.mode !== 'current' && !!display.value.strike);
const showPerk = computed(() =>
	props.mode !== 'current'
	&& displayedOption.value?.cycle === BILLING_CYCLE_SIX_MONTH
	&& perk.value?.remaining > 0
);

const providerName = computed(() =>
	props.mode === 'current' && subscriptionStore.isOrgProvidedPremium ? subscriptionStore.premiumProvider?.name : null
);

// A subscriber on a discount sees their list price struck through next to what they pay.
const discountedPrice = computed(() => {
	if (props.mode !== 'current' || user?.discounted == null || user?.next_billing_amount == null) {
		return null;
	}

	return user.next_billing_amount !== user.original_amount ? formatAmount(user.next_billing_amount, user.currency) : null;
});

const couponDescription = computed(() =>
	props.mode === 'summary' ? subscriptionStore.discountCoupon?.metadata?.description ?? null : null
);

const isPriceStruck = computed(() => !!discountedPrice.value || (props.mode === 'summary' && !!subscriptionStore.discountCoupon));
</script>
